import { useState, useEffect } from 'react';
import {
  AppBar, Toolbar, Box, Button, IconButton, Drawer, List,
  ListItem, ListItemButton, ListItemText, Container, Typography,
  Collapse, useMediaQuery, useTheme, Grid,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ConstructionIcon from '@mui/icons-material/Construction';
import PhoneIcon from '@mui/icons-material/Phone';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { useServices } from '../../hooks/usePublicApi';

function resolveIcon(icon) {
  if (typeof icon === 'function' || (typeof icon === 'object' && icon?.$$typeof)) return icon;
  if (typeof icon === 'string') {
    try { return require('../../admin/utils/iconMap').getIcon(icon); } catch { return null; }
  }
  return null;
}

const staticNavItems = [
  { label: 'Home', path: '/' },
  { label: 'Leistungen', megaMenu: true },
  { label: 'Referenzen', path: '/referenzen' },
  { label: 'Karriere', path: '/karriere' },
  { label: 'Über uns', path: '/uber-uns' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [serviceOpen, setServiceOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { services: navServices } = useServices();
  const navItems = staticNavItems.map((item) =>
    item.megaMenu ? { ...item, services: navServices || [] } : item
  );
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setDrawerOpen(false);
    setDropdownOpen(false);
  }, [location]);

  const navBg = scrolled || !isHome ? 'rgba(15,29,46,0.95)' : 'transparent';

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: navBg,
          backdropFilter: scrolled ? 'blur(20px) saturate(180%)' : 'none',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          borderBottom: scrolled ? '1px solid rgba(209,188,138,0.2)' : 'none',
          boxShadow: scrolled ? '0 4px 24px rgba(0, 0, 0, 0.12)' : 'none',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ height: { xs: 64, md: 72 } }}>
            <Box
              component={RouterLink}
              to="/"
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                mr: 4,
              }}
            >
              <Box sx={{ height: 32 }}>
                <img
                  src={`${process.env.PUBLIC_URL}/logo.svg`}
                  alt="Süd Ausbau"
                  style={{ height: 32, width: 'auto', display: 'block', objectFit: 'contain' }}
                />
              </Box>
            </Box>

            {/* Desktop nav */}
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
                {navItems.map((item) =>
                  item.megaMenu ? (
                    <Box
                      key={item.label}
                      onMouseEnter={() => setDropdownOpen(true)}
                      onMouseLeave={() => setDropdownOpen(false)}
                      sx={{ position: 'relative' }}
                    >
                      <Button
                        sx={{
                          color: 'white',
                          px: 2,
                          '&:hover': { color: 'primary.main' },
                        }}
                        endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
                      >
                        {item.label}
                      </Button>
                      <AnimatePresence>
                        {dropdownOpen && (
                          <div
                            style={{
                              position: 'fixed',
                              top: '72px',
                              left: 0,
                              right: 0,
                              zIndex: 100,
                            }}
                          >
                            <Container maxWidth="lg">
                              <Box
                                sx={{
                                  bgcolor: 'rgba(15,29,46,0.98)',
                                  borderRadius: 3,
                                  mt: 0.5,
                                  overflow: 'hidden',
                                  border: '1px solid rgba(209,188,138,0.25)',
                                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                                }}
                              >
                                {/* Header Banner */}
                                <Box
                                  sx={{
                                    background: 'linear-gradient(135deg, rgba(209,188,138,0.15) 0%, rgba(209,188,138,0.05) 100%)',
                                    borderBottom: '1px solid rgba(209,188,138,0.2)',
                                    px: 4,
                                    py: 2.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                  }}
                                >
                                  <Box>
                                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, mb: 0.5 }}>
                                      Unsere Leistungen
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>
                                      Innenausbau, Umbau & Sanierung — Alles aus einer Hand
                                    </Typography>
                                  </Box>
                                  <Box
                                    sx={{
                                      width: 40,
                                      height: 40,
                                      borderRadius: '50%',
                                      background: 'linear-gradient(135deg, #D1BC8A 0%, #B8A06E 100%)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      boxShadow: '0 4px 12px rgba(209, 188, 138, 0.3)',
                                    }}
                                  >
                                    <ConstructionIcon sx={{ color: '#0F1D2E', fontSize: 22 }} />
                                  </Box>
                                </Box>

                                <Box sx={{ p: 4 }}>
                                  <Grid container spacing={3}>
                                  {item.services.map((service) => {
                                    const ServiceIcon = service.icon || resolveIcon(service.icon_name);
                                    return (
                                      <Grid size={{ xs: 12, md: 4 }} key={service.id}>
                                        <Box
                                          sx={{
                                            p: 2.5,
                                            borderRadius: 2,
                                            border: '1px solid rgba(209,188,138,0.15)',
                                            background: 'linear-gradient(135deg, rgba(209,188,138,0.05) 0%, transparent 100%)',
                                            '&:hover': {
                                              borderColor: 'rgba(209,188,138,0.4)',
                                              background: 'linear-gradient(135deg, rgba(209,188,138,0.1) 0%, rgba(209,188,138,0.05) 100%)',
                                            },
                                          }}
                                        >
                                          {/* Main Service */}
                                          <Button
                                            component={RouterLink}
                                            to={service.path}
                                            sx={{
                                              width: '100%',
                                              justifyContent: 'flex-start',
                                              p: 0,
                                              mb: 2,
                                              '&:hover': {
                                                bgcolor: 'transparent',
                                              },
                                            }}
                                          >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                                              <Box
                                                sx={{
                                                  width: 48,
                                                  height: 48,
                                                  borderRadius: 2,
                                                  background: 'linear-gradient(135deg, #D1BC8A 0%, #B8A06E 100%)',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  flexShrink: 0,
                                                }}
                                              >
                                                {ServiceIcon && <ServiceIcon sx={{ color: '#0F1D2E', fontSize: 24 }} />}
                                              </Box>
                                              <Box sx={{ textAlign: 'left', flex: 1 }}>
                                                <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, fontSize: '1.1rem' }}>
                                                  {service.title}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem' }}>
                                                  {service.subtitle}
                                                </Typography>
                                              </Box>
                                            </Box>
                                          </Button>

                                          {/* Sub-services */}
                                          <Box sx={{ pl: 1 }}>
                                            {service.features.slice(0, 4).map((feature, idx) => {
                                              const FeatureIcon = feature.icon || resolveIcon(feature.icon_name);
                                              return (
                                                <Button
                                                  key={idx}
                                                  component={RouterLink}
                                                  to={service.path}
                                                  sx={{
                                                    width: '100%',
                                                    justifyContent: 'flex-start',
                                                    py: 1,
                                                    px: 1.5,
                                                    mb: 0.5,
                                                    borderRadius: 1.5,
                                                    '&:hover': {
                                                      bgcolor: 'rgba(209,188,138,0.1)',
                                                    },
                                                  }}
                                                >
                                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                                                    {FeatureIcon && <FeatureIcon sx={{ color: 'primary.main', fontSize: 18, flexShrink: 0 }} />}
                                                    <Typography
                                                      variant="body2"
                                                      sx={{
                                                        color: 'rgba(255,255,255,0.85)',
                                                        fontSize: '0.85rem',
                                                        fontWeight: 500,
                                                        textAlign: 'left',
                                                      }}
                                                    >
                                                      {feature.title}
                                                    </Typography>
                                                  </Box>
                                                </Button>
                                              );
                                            })}
                                            {service.features.length > 4 && (
                                              <Button
                                                component={RouterLink}
                                                to={service.path}
                                                sx={{
                                                  width: '100%',
                                                  justifyContent: 'flex-start',
                                                  py: 1,
                                                  px: 1.5,
                                                  color: 'primary.main',
                                                  fontSize: '0.8rem',
                                                  fontWeight: 600,
                                                }}
                                              >
                                                + {service.features.length - 4} weitere Leistungen →
                                              </Button>
                                            )}
                                          </Box>
                                        </Box>
                                      </Grid>
                                    );
                                  })}
                                  </Grid>
                                </Box>
                              </Box>
                            </Container>
                          </div>
                        )}
                      </AnimatePresence>
                    </Box>
                  ) : (
                    <Button
                      key={item.path}
                      component={RouterLink}
                      to={item.path}
                      sx={{
                        color: location.pathname === item.path ? 'primary.main' : 'white',
                        px: 2,
                        '&:hover': { color: 'primary.main' },
                      }}
                    >
                      {item.label}
                    </Button>
                  )
                )}
              </Box>
            )}

            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
              {!isMobile && (
                <>
                  <Button
                    component="a"
                    href="tel:+498932402433"
                    startIcon={<PhoneIcon sx={{ fontSize: '16px !important' }} />}
                    sx={{
                      color: 'rgba(255,255,255,0.75)',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      px: 1.5,
                      '&:hover': { color: 'primary.main' },
                      letterSpacing: '0.01em',
                    }}
                  >
                    089 32402433
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    component={RouterLink}
                    to="/anfrage"
                    size="small"
                    sx={{ px: 1.75, py: 0.6, fontSize: '0.8rem', minHeight: 0 }}
                  >
                    Anfrage
                  </Button>
                </>
              )}
              {isMobile && (
                <IconButton
                  onClick={() => setDrawerOpen(true)}
                  sx={{ color: 'white' }}
                >
                  <MenuIcon />
                </IconButton>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: 360,
            bgcolor: 'secondary.main',
            color: 'white',
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <List sx={{ px: 2 }}>
          {navItems.map((item) =>
            item.megaMenu ? (
              <Box key={item.label}>
                <ListItemButton onClick={() => setServiceOpen(!serviceOpen)}>
                  <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 600 }} />
                  {serviceOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={serviceOpen}>
                  <List disablePadding>
                    {item.services.map((service) => {
                      const ServiceIcon = service.icon || resolveIcon(service.icon_name);
                      return (
                        <Box key={service.id}>
                          <ListItemButton
                            component={RouterLink}
                            to={service.path}
                            sx={{
                              pl: 3,
                              py: 1.5,
                              borderLeft: '3px solid',
                              borderColor: 'primary.main',
                              ml: 2,
                              mb: 1,
                            }}
                          >
                            {ServiceIcon && <ServiceIcon sx={{ color: 'primary.main', mr: 1.5, fontSize: 22 }} />}
                            <ListItemText
                              primary={service.title}
                              secondary={service.subtitle}
                              primaryTypographyProps={{ fontWeight: 600, fontSize: '0.95rem' }}
                              secondaryTypographyProps={{ sx: { color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem' } }}
                            />
                          </ListItemButton>
                          <List disablePadding sx={{ pl: 6, mb: 1 }}>
                            {service.features.slice(0, 3).map((feature, idx) => {
                              const FeatureIcon = feature.icon || resolveIcon(feature.icon_name);
                              return (
                                <ListItemButton
                                  key={idx}
                                  component={RouterLink}
                                  to={service.path}
                                  sx={{ py: 0.75, minHeight: 'auto' }}
                                >
                                  {FeatureIcon && <FeatureIcon sx={{ color: 'rgba(209,188,138,0.6)', fontSize: 16, mr: 1 }} />}
                                  <ListItemText
                                    primary={feature.title}
                                    primaryTypographyProps={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)' }}
                                  />
                                </ListItemButton>
                              );
                            })}
                          </List>
                        </Box>
                      );
                    })}
                  </List>
                </Collapse>
              </Box>
            ) : (
              <ListItem key={item.path} disablePadding>
                <ListItemButton component={RouterLink} to={item.path}>
                  <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 600 }} />
                </ListItemButton>
              </ListItem>
            )
          )}
          <ListItem disablePadding sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              component={RouterLink}
              to="/anfrage"
              size="large"
            >
              Anfrage senden
            </Button>
          </ListItem>
          <ListItem disablePadding sx={{ mt: 1 }}>
            <Button
              fullWidth
              component="a"
              href="tel:+498932402433"
              startIcon={<PhoneIcon />}
              sx={{
                color: 'rgba(255,255,255,0.7)',
                fontSize: '0.9rem',
                fontWeight: 600,
                justifyContent: 'center',
                '&:hover': { color: 'primary.main' },
              }}
            >
              089 32402433
            </Button>
          </ListItem>
        </List>
      </Drawer>
    </>
  );
}
